import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import useTranslation from "../../../hooks/useTranslation";
import { useGeneratedTextCreateSpeech } from "../../../hooks/useGeneratedTextUpdateSpeech";
import { useGeneratedTextDetails } from "../../../hooks/useGeneratedTextDetails";
import { useIncrementGeneratedTextViewCounter } from "../../../hooks/useIncrementGeneratedTextViewCounter";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  usePaginatedVocabularyList,
  VocabularyDto,
} from "../../../hooks/usePaginatedVocabularyList";
import { useChapterCreate } from "../../../hooks/useChapterCreate";
import { GeneratedTextDetailsTextPane } from "../../../components/GeneratedTextDetailsTextPane";
import { LanguageHelperPanel } from "../../../components/LanguageHelperPanel";
import { VocabularyPreviewCard } from "../../../components/VocabularyPreviewCard";
import { useBarkAccountQuery } from "../../../hooks/useBarkAccountQuery";
import { useVocabularyUpdate } from "../../../hooks/useVocabularyUpdate";
import { SubmitHandler, useForm } from "react-hook-form";
import { useChapterRetrieveQuery } from "../../../hooks/useChapterRetrieveQuery";
import { useLanguageSpace } from "../../../hooks/useLanguageSpace";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import {
  getVocabularyBaseWord,
  getVocabularyPrimaryWord,
  getVocabularyPrimaryTranslation,
  getVocabularySurfaceWord,
  normalizeVocabularyTranslationDetails,
} from "../../../utils/vocabularyTranslationDetails";
import { highlightVocabularyWordInSentence } from "../../../utils/highlightVocabularyWordInSentence";
import { VocabularyPronunciationButton } from "../../../components/VocabularyPronunciationButton";
import { resolveGeneratedTextContent } from "../../../utils/generatedTextContent";
import { getChapterContinueButtonLabelKey } from "../../../utils/getChapterContinueButtonLabelKey";
import { resolveActiveTextHighlightWords } from "../../../utils/resolveActiveTextHighlightWords";
import { resolveSelectedVocabularyWordsForChapterGeneration } from "../../../utils/resolveSelectedVocabularyWordsForChapterGeneration";
import { resolveWordAddedToastKey } from "../../../utils/resolveWordAddedToastKey";
import { shouldRefetchVocabularyAfterCompletion } from "../../../utils/shouldRefetchVocabularyAfterCompletion";
import { resolveUnresolvedStruggleVocabularyCards } from "../../../utils/resolveUnresolvedStruggleVocabularyCards";
import {
  pruneVocabularySelectionToExistingIds,
  resolveDefaultSelectedVocabularyIdsForGeneratedReinforcementText,
} from "../../../utils/vocabularySelectionState";
import { resetAppScrollPosition } from "../../../utils/resetAppScrollPosition";
import { notifySnackbar } from "../../../lib/notifySnackbar";
import { getCsrfCookie } from "../../../lib/utils";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  LightBulbIcon,
} from "@heroicons/react/24/outline";
import { PauseIcon, PlayIcon } from "@heroicons/react/24/solid";

export const Route = createFileRoute("/app/reeprise/$generatedTextId")({
  component: GeneratedTextDetailsPage,
  validateSearch: (search) => {
    const parsedFocusToken = Number(search.focusToken);
    return {
      chapter:
        search.chapter === undefined ? undefined : Number(search.chapter),
      focusWord:
        typeof search.focusWord === "string" ? search.focusWord : undefined,
      focusSentence:
        typeof search.focusSentence === "string"
          ? search.focusSentence
          : undefined,
      focusToken: Number.isFinite(parsedFocusToken)
        ? parsedFocusToken
        : undefined,
    };
  },
});

function GeneratedTextDetailsPage() {
  const { translate } = useTranslation();
  const { generatedTextId } = Route.useParams();
  const { chapter, focusWord, focusSentence, focusToken } = Route.useSearch();
  const { createSpeechMutation } = useGeneratedTextCreateSpeech();
  const reepriseDetails = useGeneratedTextDetails(Number(generatedTextId));
  const { barkAccountQuery } = useBarkAccountQuery();
  const { languageSpace } = useLanguageSpace("ENGLISH");
  const chapterDetails = useChapterRetrieveQuery({
    chapterCounter: chapter ?? 0,
    generatedTextId: Number(generatedTextId),
  });
  const incrementCounter = useIncrementGeneratedTextViewCounter({
    textId: Number(generatedTextId),
  });

  const topRef = useRef<HTMLDivElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioProgressRef = useRef<HTMLDivElement | null>(null);

  const [selectedVocabularyIds, setSelectedVocabularyIds] = useState<number[]>(
    [],
  );
  const [deselectedVocabularyIds, setDeselectedVocabularyIds] = useState<
    number[]
  >([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [isAudioScrubbing, setIsAudioScrubbing] = useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioPlaybackRate, setAudioPlaybackRate] = useState(1);
  const [showPodcastTranscript, setShowPodcastTranscript] = useState(false);
  const showAudioLoading = isAudioLoading || createSpeechMutation.isPending;
  const [recentVocabulary, setRecentVocabulary] = useState<
    { word: string; translation?: string | null }[]
  >([]);
  const [optimisticSelectedWordsById, setOptimisticSelectedWordsById] = useState<
    Record<number, string>
  >({});
  const [titleOnlyVocabularyIds, setTitleOnlyVocabularyIds] = useState<number[]>([]);
  const [focusRequest, setFocusRequest] = useState<{
    word: string;
    sentence?: string | null;
    token: number;
  } | null>(null);
  const [grammarLesson, setGrammarLesson] = useState<{
    title: string;
    summary: string;
    points: { pattern: string; example: string; explanation: string }[];
  } | null>(null);
  const [isGrammarLessonLoading, setIsGrammarLessonLoading] = useState(false);
  const [grammarLessonError, setGrammarLessonError] = useState<string | null>(null);
  const [showTextScopedTrainer, setShowTextScopedTrainer] = useState(false);
  const [pendingChapterGeneratedWords, setPendingChapterGeneratedWords] = useState<
    string[]
  >([]);
  const audioSrc =
    chapter !== undefined
      ? `/streams/audio/${generatedTextId}/?chapter=${chapter}`
      : `/streams/audio/${generatedTextId}/`;

  const onVocabularyCreate = (
    vocabularyDto: VocabularyDto,
    options?: { source: "title" | "text" },
  ) => {
    notifySnackbar({
      message: translate(resolveWordAddedToastKey(vocabularyDto.word)),
    });
    setSelectedVocabularyIds((oldValue) =>
      oldValue.includes(vocabularyDto.id)
        ? oldValue
        : [...oldValue, vocabularyDto.id],
    );
    setDeselectedVocabularyIds((prev) =>
      prev.filter((entry) => entry !== vocabularyDto.id),
    );
    if (options?.source === "title") {
      setTitleOnlyVocabularyIds((prev) =>
        prev.includes(vocabularyDto.id) ? prev : [...prev, vocabularyDto.id],
      );
    }
    if (vocabularyDto.id && vocabularyDto.word?.trim()) {
      setOptimisticSelectedWordsById((prev) => ({
        ...prev,
        [vocabularyDto.id]: vocabularyDto.word.trim(),
      }));
    }
    if (vocabularyDto.word?.trim()) {
      setRecentVocabulary((prev) => [
        { word: vocabularyDto.word, translation: vocabularyDto.translation },
        ...prev.filter((item) => item.word !== vocabularyDto.word),
      ].slice(0, 5));
    }
  };

  useEffect(() => {
    resetAppScrollPosition();
    setSelectedVocabularyIds([]);
    setDeselectedVocabularyIds([]);
    setOptimisticSelectedWordsById({});
    setTitleOnlyVocabularyIds([]);
    setShowTextScopedTrainer(false);
    setPendingChapterGeneratedWords([]);
  }, [generatedTextId, chapter]);

  useEffect(() => {
    if (!focusWord && !focusSentence) {
      return;
    }
    setFocusRequest({
      word: focusWord ?? "",
      sentence: focusSentence ?? null,
      token: focusToken ?? Date.now(),
    });
  }, [focusSentence, focusToken, focusWord]);

  useEffect(() => {
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
    setIsPlaying(false);
    setIsAudioLoading(false);
    setIsAudioScrubbing(false);
    setAudioCurrentTime(0);
    setAudioDuration(0);
    setAudioPlaybackRate(1);
    setShowPodcastTranscript(false);
  }, [generatedTextId, chapter]);

  const chapterCreateMutation = useChapterCreate({
    generatedTextId: Number(generatedTextId),
  });

  const paginatedVocabularyList = usePaginatedVocabularyList({
    generated_text_id: Number(generatedTextId),
  });
  const completionVocabularyRefetchKeyRef = useRef<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const id = setTimeout(() => {
      void incrementCounter.mutateAsync();
    }, 5_000);

    return () => clearTimeout(id);
  }, [generatedTextId, incrementCounter]);

  useEffect(() => {
    const completionRefetchKey = `${generatedTextId}:${chapter ?? "root"}`;
    if (
      !shouldRefetchVocabularyAfterCompletion({
        status: reepriseDetails.data?.status,
        completionRefetchKey,
        lastRefetchedCompletionKey: completionVocabularyRefetchKeyRef.current,
      })
    ) {
      return;
    }
    completionVocabularyRefetchKeyRef.current = completionRefetchKey;
    void paginatedVocabularyList.refetch();
  }, [
    chapter,
    generatedTextId,
    paginatedVocabularyList,
    reepriseDetails.data?.status,
  ]);

  const vocabularies =
    paginatedVocabularyList.data?.pages.flatMap((page) => page.results) || [];
  const resolvedDetailsTitle = resolveGeneratedTextContent({
    rawText:
      reepriseDetails.data && "chatgpt_output" in reepriseDetails.data
        ? reepriseDetails.data.chatgpt_output ?? ""
        : "",
    fallbackTitle: reepriseDetails.data?.topic ?? "",
    generatingTitleCopy: translate("text_card_generating_title"),
    isComplete: reepriseDetails.data?.status === "COMPLETE",
  }).displayTitle.trim();
  const isPodcastText = reepriseDetails.data?.text_type === "PODCAST";
  const podcastTranscript = useMemo(() => {
    if (chapter !== undefined) {
      if (
        chapterDetails.data &&
        chapterDetails.data.status === "COMPLETE" &&
        "content" in chapterDetails.data
      ) {
        return chapterDetails.data.content.trim();
      }
      return "";
    }
    const rawText =
      reepriseDetails.data && "chatgpt_output" in reepriseDetails.data
        ? reepriseDetails.data.chatgpt_output ?? ""
        : "";
    return resolveGeneratedTextContent({
      rawText,
      fallbackTitle: reepriseDetails.data?.topic ?? "",
      generatingTitleCopy: translate("text_card_generating_title"),
      isComplete: reepriseDetails.data?.status === "COMPLETE",
    }).displayStory.trim();
  }, [chapter, chapterDetails.data, reepriseDetails.data, translate]);
  const resolvedDisplayText = useMemo(() => {
    if (chapter !== undefined) {
      if (
        chapterDetails.data &&
        chapterDetails.data.status === "COMPLETE" &&
        "content" in chapterDetails.data
      ) {
        return chapterDetails.data.content.trim();
      }
      return "";
    }
    const rawText =
      reepriseDetails.data && "chatgpt_output" in reepriseDetails.data
        ? reepriseDetails.data.chatgpt_output ?? ""
        : "";
    return resolveGeneratedTextContent({
      rawText,
      fallbackTitle: reepriseDetails.data?.topic ?? "",
      generatingTitleCopy: translate("text_card_generating_title"),
      isComplete: reepriseDetails.data?.status === "COMPLETE",
    }).displayStory.trim();
  }, [chapter, chapterDetails.data, reepriseDetails.data, translate]);
  const persistedTitleOnlyVocabularyIds = useMemo(
    () =>
      vocabularies
        .filter((voc) => voc.sentence?.trim() === resolvedDetailsTitle)
        .map((voc) => voc.id),
    [resolvedDetailsTitle, vocabularies],
  );
  const selectedVocabularyWords = useMemo(
    () =>
      vocabularies
        .filter(
          (voc) =>
            selectedVocabularyIds.includes(voc.id) &&
            !titleOnlyVocabularyIds.includes(voc.id) &&
            !persistedTitleOnlyVocabularyIds.includes(voc.id),
        )
        .map((voc) => voc.word),
    [
      vocabularies,
      selectedVocabularyIds,
      titleOnlyVocabularyIds,
      persistedTitleOnlyVocabularyIds,
    ],
  );
  const titleVocabularyWords = useMemo(() => {
    const persistedTitleVocabularyWords = vocabularies
      .filter((voc) => persistedTitleOnlyVocabularyIds.includes(voc.id))
      .map((voc) => voc.word);
    const localTitleVocabularyWords = titleOnlyVocabularyIds
      .filter((id) => !persistedTitleOnlyVocabularyIds.includes(id))
      .map((id) => optimisticSelectedWordsById[id] ?? "");
    return [
      ...persistedTitleVocabularyWords,
      ...localTitleVocabularyWords,
    ].filter((word): word is string => Boolean(word?.trim()));
  }, [
    optimisticSelectedWordsById,
    persistedTitleOnlyVocabularyIds,
    titleOnlyVocabularyIds,
    vocabularies,
  ]);
  const selectedVocabularyWordsForGeneration = useMemo(
    () => {
      const struggleFallbackWords =
        reepriseDetails.data?.origin === "STRUGGLE_PRACTICE"
          ? (reepriseDetails.data?.struggle_words ?? [])
              .map((entry) => entry.word?.trim() ?? "")
              .filter((word): word is string => Boolean(word))
          : [];
      return resolveSelectedVocabularyWordsForChapterGeneration({
        optimisticWordsById: optimisticSelectedWordsById,
        selectedVocabularyIds,
        vocabularies,
        fallbackWords: struggleFallbackWords,
        displayText: resolvedDisplayText,
        generatedTextId: Number(generatedTextId),
        chapterCounter: chapter,
      });
    },
    [
      chapter,
      generatedTextId,
      optimisticSelectedWordsById,
      reepriseDetails.data?.origin,
      reepriseDetails.data?.struggle_words,
      resolvedDisplayText,
      selectedVocabularyIds,
      vocabularies,
    ],
  );
  const chapterContinueButtonLabelKey = getChapterContinueButtonLabelKey(
    selectedVocabularyWordsForGeneration.length,
  );
  const activeHighlightWords = resolveActiveTextHighlightWords({
    selectedVocabularyWords,
    titleVocabularyWords,
    chapterGeneratedWords:
      chapter !== undefined &&
      chapterDetails.data &&
      chapterDetails.data.status !== "NON_EXISTENT"
        ? chapterDetails.data.generated_words
        : undefined,
    pendingChapterGeneratedWords:
      chapter !== undefined ? pendingChapterGeneratedWords : undefined,
  });
  const struggleVocabularyCards: VocabularyDto[] =
    reepriseDetails.isSuccess && Array.isArray(reepriseDetails.data?.struggle_words)
      ? reepriseDetails.data.struggle_words
          .filter(
            (entry) => entry.word_language === reepriseDetails.data?.language,
          )
          .map((entry, idx) => ({
            id: typeof entry.id === "number" ? entry.id : -1000 - idx,
            word: entry.word,
            word_language: entry.word_language,
            translation_language: entry.translation_language ?? "",
            translation: entry.translation,
            winning_streak: -1,
            last_tested: new Date().toISOString(),
            mnemonic: "",
            sentence: entry.sentence,
            status: "COMPLETE",
            origin: "STRUGGLE_WORD",
            usages: [],
          }))
      : [];
  const unresolvedStruggleVocabularyCards = useMemo(
    () =>
      resolveUnresolvedStruggleVocabularyCards({
        struggleVocabularyCards,
        vocabularies,
      }),
    [struggleVocabularyCards, vocabularies],
  );
  const showStruggleWords =
    reepriseDetails.data?.origin === "STRUGGLE_PRACTICE" &&
    unresolvedStruggleVocabularyCards.length > 0;
  const supportEmail =
    barkAccountQuery.data?.support_email?.trim() || "office@uxhulu.com";
  const failedContactHref = `mailto:${supportEmail}?subject=${encodeURIComponent(
    "Generated text incident",
  )}&body=${encodeURIComponent(
    `Hi,\n\nI could not open generated text #${generatedTextId} because the text generation failed.\n\nPlease let me know when it is fixed.\n`,
  )}`;
  const hasSameIds = (left: number[], right: number[]) =>
    left.length === right.length &&
    left.every((value, index) => value === right[index]);

  const seekAudioByClientX = (clientX: number) => {
    const progressEl = audioProgressRef.current;
    const audioEl = audioRef.current;
    if (!progressEl || !audioEl) {
      return;
    }
    const duration = Number.isFinite(audioEl.duration) ? audioEl.duration : 0;
    if (duration <= 0) {
      return;
    }
    const bounds = progressEl.getBoundingClientRect();
    if (bounds.width <= 0) {
      return;
    }
    const ratio = Math.min(1, Math.max(0, (clientX - bounds.left) / bounds.width));
    const nextTime = ratio * duration;
    audioEl.currentTime = nextTime;
    setAudioCurrentTime(nextTime);
  };

  useEffect(() => {
    const dailyNudgeStruggleWordIds =
      reepriseDetails.data?.struggle_words
        ?.map((entry) => entry.id)
        .filter((id): id is number => typeof id === "number") ?? [];
    const availableVocabularyIds = vocabularies.map((entry) => entry.id);
    const preselectedIds =
      resolveDefaultSelectedVocabularyIdsForGeneratedReinforcementText({
      origin: reepriseDetails.data?.origin,
      struggleWordIds: dailyNudgeStruggleWordIds,
      availableIds: availableVocabularyIds,
      selectedIds: selectedVocabularyIds,
      deselectedIds: deselectedVocabularyIds,
    });
    const prunedSelection = pruneVocabularySelectionToExistingIds({
      selectedIds: preselectedIds,
      deselectedIds: deselectedVocabularyIds,
      availableIds: availableVocabularyIds,
    });
    setSelectedVocabularyIds((prev) => {
      const next = prunedSelection.selectedIds;
      return hasSameIds(next, prev) ? prev : next;
    });
    setDeselectedVocabularyIds((prev) => {
      const next = prunedSelection.deselectedIds;
      return hasSameIds(next, prev) ? prev : next;
    });
  }, [
    deselectedVocabularyIds,
    reepriseDetails.data?.origin,
    reepriseDetails.data?.struggle_words,
    selectedVocabularyIds,
    vocabularies,
  ]);

  useEffect(() => {
    if (!isAudioScrubbing) {
      return;
    }
    const onMouseMove = (event: MouseEvent) => {
      seekAudioByClientX(event.clientX);
    };
    const onMouseUp = () => {
      setIsAudioScrubbing(false);
    };
    const onTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      seekAudioByClientX(touch.clientX);
    };
    const onTouchEnd = () => {
      setIsAudioScrubbing(false);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("touchmove", onTouchMove);
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchEnd);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [isAudioScrubbing]);

  const createChapterFromSelection = async () => {
    const words = selectedVocabularyWordsForGeneration;
    setPendingChapterGeneratedWords(words);
    const createdChapter = await chapterCreateMutation.mutateAsync({
      words,
    });
    navigate({
      to: "/app/reeprise/$generatedTextId",
      params: {
        generatedTextId: generatedTextId,
      },
      search: {
        chapter: createdChapter.counter,
        focusWord: undefined,
        focusSentence: undefined,
        focusToken: undefined,
      },
    });
  };

  const toggleTrainerForCurrentText = () => {
    setShowTextScopedTrainer((prev) => !prev);
  };

  const generateGrammarLesson = async () => {
    setIsGrammarLessonLoading(true);
    setGrammarLessonError(null);
    try {
      const res = await fetch(
        `/api/reeprise/generated_texts/${generatedTextId}/grammar_lesson/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFTOKEN": getCsrfCookie(),
          },
          body: JSON.stringify({}),
        },
      );
      if (!res.ok) {
        throw new Error("grammar_lesson_request_failed");
      }
      const payload = (await res.json()) as {
        title?: string;
        summary?: string;
        points?: { pattern?: string; example?: string; explanation?: string }[];
      };
      setGrammarLesson({
        title: payload.title?.trim() || "",
        summary: payload.summary?.trim() || "",
        points: Array.isArray(payload.points)
          ? payload.points
              .map((point) => ({
                pattern: point.pattern?.trim() || "",
                example: point.example?.trim() || "",
                explanation: point.explanation?.trim() || "",
              }))
              .filter(
                (point) =>
                  point.pattern.length > 0 ||
                  point.example.length > 0 ||
                  point.explanation.length > 0,
              )
          : [],
      });
    } catch {
      setGrammarLessonError(translate("generated_text_grammar_lesson_error"));
      notifySnackbar({
        message: translate("generated_text_grammar_lesson_error"),
      });
    } finally {
      setIsGrammarLessonLoading(false);
    }
  };

  if (reepriseDetails.data?.status === "FAILED") {
    return (
      <FailedGeneratedTextScreen
        contactHref={failedContactHref}
        generatedTextId={Number(generatedTextId)}
        onRefresh={() => {
          void reepriseDetails.refetch();
        }}
        supportEmail={supportEmail}
      />
    );
  }

  return (
    <>
      <div
        ref={topRef}
        className=" bg-neutral-50 px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] text-left md:h-full md:pb-0 place-items-center"
      >
        <div className="md:grid md:grid-cols-12 gap-11 md:h-full md:mr-10">
          <div className={"relative md:p-4 rounded-lg md:h-full md:col-span-6 md:col-start-3 mb-9"}>
            <div className="mt-2 mb-2 flex items-center justify-between text-slate-700">
              <LastChapterNavigationTile
                activeChapterNumber={chapter}
                generatedTextId={Number(generatedTextId)}
              />
              <NextChapterNavigationTile
                activeChapterNumber={chapter}
                generatedTextId={Number(generatedTextId)}
              />
            </div>
            <div className="mt-3 border-y border-slate-200 py-2">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    createSpeechMutation.mutate({
                      id: Number(generatedTextId),
                      chapter_number: chapter,
                    });
                    const el = audioRef.current;
                    if (!el) return;
                    if (isPlaying) {
                      el.pause();
                    } else {
                      setIsAudioLoading(true);
                      void el.play().catch((err) => {
                        console.error("Audio play error", err);
                        setIsAudioLoading(false);
                      });
                    }
                  }}
                  className="h-8 w-8 rounded-full border border-slate-300 bg-white text-slate-700 grid place-items-center hover:bg-slate-50"
                  aria-label={
                    isPlaying
                      ? translate("audio_player_pause")
                      : translate("audio_player_play")
                  }
                >
                  {isPlaying ? (
                    <PauseIcon className="h-4 w-4" />
                  ) : (
                    <PlayIcon className="h-4 w-4 pl-[1px]" />
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  {isPodcastText && (
                    <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
                      {translate("podcast_details_meta_line")}
                    </p>
                  )}
                  <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
                    {translate("audio_player_title")}
                  </p>
                  <p className="font-serif text-sm text-slate-700 flex items-center gap-2">
                    {showAudioLoading && (
                      <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-400 border-t-transparent" />
                    )}
                    {audioDuration > 0
                      ? `${formatAudioClock(audioCurrentTime)} / ${formatAudioClock(audioDuration)}`
                      : showAudioLoading
                        ? translate("audio_player_loading")
                        : isPlaying
                          ? translate("audio_player_playing")
                          : isPodcastText
                            ? translate("podcast_details_audio_idle")
                            : translate("audio_player_idle")}
                  </p>
                </div>
                <button
                  type="button"
                  className="rounded-full border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
                  onClick={() => {
                    const nextRate =
                      audioPlaybackRate === 1
                        ? 1.25
                        : audioPlaybackRate === 1.25
                          ? 1.5
                          : 1;
                    const el = audioRef.current;
                    if (el) {
                      el.playbackRate = nextRate;
                    }
                    setAudioPlaybackRate(nextRate);
                  }}
                >
                  {`${audioPlaybackRate}x`}
                </button>
              </div>
              <div
                ref={audioProgressRef}
                className="relative mt-2 h-4 w-full cursor-pointer"
                onMouseDown={(event) => {
                  seekAudioByClientX(event.clientX);
                  setIsAudioScrubbing(true);
                }}
                onTouchStart={(event) => {
                  const touch = event.touches[0];
                  if (!touch) {
                    return;
                  }
                  seekAudioByClientX(touch.clientX);
                  setIsAudioScrubbing(true);
                }}
                role="slider"
                aria-label={translate("audio_player_title")}
                aria-valuemin={0}
                aria-valuemax={audioDuration || 0}
                aria-valuenow={audioCurrentTime}
              >
                <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-slate-200" />
                <div
                  className="absolute left-0 top-1/2 h-px -translate-y-1/2 bg-slate-500 transition-[width] duration-200"
                  style={{
                    width:
                      audioDuration > 0
                        ? `${Math.min(100, (audioCurrentTime / audioDuration) * 100)}%`
                        : "0%",
                  }}
                />
                <button
                  type="button"
                  className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-slate-400 bg-white shadow-sm hover:scale-105"
                  style={{
                    left:
                      audioDuration > 0
                        ? `${Math.min(100, (audioCurrentTime / audioDuration) * 100)}%`
                        : "0%",
                  }}
                  onMouseDown={(event) => {
                    event.stopPropagation();
                    seekAudioByClientX(event.clientX);
                    setIsAudioScrubbing(true);
                  }}
                  onTouchStart={(event) => {
                    event.stopPropagation();
                    const touch = event.touches[0];
                    if (!touch) {
                      return;
                    }
                    seekAudioByClientX(touch.clientX);
                    setIsAudioScrubbing(true);
                  }}
                  aria-label={translate("audio_player_title")}
                />
              </div>
              <audio
                key={audioSrc}
                ref={audioRef}
                src={audioSrc}
                preload="none"
                playsInline
                className="hidden"
                onPlay={() => {
                  // Keep loading state until audio is actually playing.
                }}
                onPlaying={() => {
                  setIsPlaying(true);
                  setIsAudioLoading(false);
                }}
                onTimeUpdate={(event) =>
                  setAudioCurrentTime(event.currentTarget.currentTime || 0)
                }
                onLoadedMetadata={(event) => {
                  setAudioDuration(event.currentTarget.duration || 0);
                  setAudioPlaybackRate(event.currentTarget.playbackRate || 1);
                }}
                onWaiting={() => setIsAudioLoading(true)}
                onStalled={() => setIsAudioLoading(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => {
                  setIsPlaying(false);
                  setAudioCurrentTime(audioDuration || 0);
                }}
                onError={() => setIsAudioLoading(false)}
              />
              {isPodcastText && (
                <div className="mt-2">
                  <button
                    type="button"
                    className="text-xs font-semibold text-slate-600 underline underline-offset-2 hover:text-slate-800"
                    onClick={() => setShowPodcastTranscript((prev) => !prev)}
                  >
                    {showPodcastTranscript
                      ? translate("podcast_details_hide_transcript")
                      : translate("podcast_details_show_transcript")}
                  </button>
                </div>
              )}
              {isPodcastText && showPodcastTranscript && (
                <div className="mt-2 max-h-64 overflow-auto rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-ABee-zee leading-tight whitespace-pre-line text-slate-700">
                    {podcastTranscript || translate("podcast_details_transcript_pending")}
                  </p>
                </div>
              )}
            </div>
            <GeneratedTextDetailsTextPane
              generatedTextId={Number(generatedTextId)}
              chapterNumber={chapter}
              onVocabularyCreate={onVocabularyCreate}
              highlightWords={activeHighlightWords}
              focusRequest={focusRequest}
            />
          </div>
          <div
            className={
              "bg-white-300 rounded-lg md:col-start-9 md:col-span-4 md:h-full "
            }
          >
            <div className="mb-4">
              <LanguageHelperPanel
                generatedTextId={Number(generatedTextId)}
                chapterNumber={chapter}
                wordLanguage={
                  languageSpace !== "ALL"
                    ? languageSpace
                    : reepriseDetails.data?.language
                }
                contextText={
                  chapter !== undefined
                    ? chapterDetails.data &&
                      "content" in chapterDetails.data &&
                      chapterDetails.data.status === "COMPLETE"
                      ? chapterDetails.data.content
                      : undefined
                    : reepriseDetails.data &&
                        "chatgpt_output" in reepriseDetails.data
                      ? reepriseDetails.data.chatgpt_output
                      : undefined
                }
                recentVocabulary={recentVocabulary}
              />
            </div>
            <h2 className="text-l bg-neutral-50 md:pt-5 font-semibold md:mb-1 mb-4 md:sticky top-0 z-10">
              {translate("vocabulary")}
            </h2>
            {vocabularies.length === 0 && (
              <div className="flex bg-slate-100 mb-4 p-3 rounded-md mt-4">
                <p>
                  <LightBulbIcon className="w-6 h-6 mr-1" />
                </p>
                {translate("translations_click_on_a_word_to_translate_it")}
              </div>
            )}
            {reepriseDetails.isSuccess && (
              <div className="mb-3 mt-1 border-y border-slate-200 py-2">
                <h3 className="mb-2 text-[11px] uppercase tracking-[0.14em] text-slate-700">
                  {translate("generated_text_grammar_lesson_title")}
                </h3>
                <button
                  data-testid="grammar-lesson-generate-button"
                  className="w-full rounded-full border border-slate-300 bg-white px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-700 hover:bg-slate-50 cursor-pointer"
                  onClick={() => {
                    void generateGrammarLesson();
                  }}
                  disabled={isGrammarLessonLoading || reepriseDetails.data?.status !== "COMPLETE"}
                >
                  {isGrammarLessonLoading
                    ? translate("generated_text_grammar_lesson_generating")
                    : translate("generated_text_grammar_lesson_generate")}
                </button>
                {grammarLessonError && (
                  <p className="mt-2 text-xs text-rose-700">{grammarLessonError}</p>
                )}
                {grammarLesson && (
                  <div
                    data-testid="grammar-lesson-body"
                    className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"
                  >
                    {grammarLesson.title && (
                      <p className="font-semibold text-slate-900">{grammarLesson.title}</p>
                    )}
                    {grammarLesson.summary && (
                      <p className="mt-1 whitespace-pre-line">{grammarLesson.summary}</p>
                    )}
                    {grammarLesson.points.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {grammarLesson.points.map((point, index) => (
                          <div key={`${point.pattern}-${index}`} className="rounded-md border border-slate-200 bg-white p-2">
                            {point.pattern && (
                              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                                {point.pattern}
                              </p>
                            )}
                            {point.example && (
                              <p className="mt-1 italic text-slate-700">{point.example}</p>
                            )}
                            {point.explanation && (
                              <p className="mt-1 text-slate-700">{point.explanation}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {reepriseDetails.isSuccess && (
              <div className="mb-3 mt-1 border-y border-slate-200 py-2">
                <button
                  className="w-full rounded-full border border-slate-300 bg-white px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-700 hover:bg-slate-50 cursor-pointer"
                  onClick={toggleTrainerForCurrentText}
                >
                  {translate(
                    showTextScopedTrainer
                      ? "trainer_hide_for_this_text"
                      : "trainer_start_for_this_text",
                  )}
                </button>
              </div>
            )}
            {showTextScopedTrainer && (
              <div className="mb-4">
                <VocabularyPreviewCard generatedTextId={Number(generatedTextId)} />
              </div>
            )}
            {reepriseDetails.isSuccess && (
              <div className="mb-3 mt-1 hidden border-y border-slate-200 py-2 md:block">
                <button
                  className="w-full rounded-full border border-slate-300 bg-white px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-700 hover:bg-slate-50 cursor-pointer"
                  onClick={() => {
                    void createChapterFromSelection();
                  }}
                >
                  {translate(chapterContinueButtonLabelKey)}
                </button>
              </div>
            )}
            <ul className="p-0 m-0 w-full z-20 md:pt-1 pb-24 md:pb-0">
              {showStruggleWords &&
                unresolvedStruggleVocabularyCards.map((arg) => (
                  <VocabularyItem
                    key={arg.id}
                    vocabulary={arg}
                    isSelected={true}
                    addOrRemoveVocabulary={() => {}}
                    onJump={() => {
                      setFocusRequest({
                        word: arg.word,
                        sentence: arg.sentence ?? null,
                        token: Date.now(),
                      });
                    }}
                  ></VocabularyItem>
                ))}
              {vocabularies.map((arg) => (
                <VocabularyItem
                  key={arg.id}
                  vocabulary={arg}
                  isSelected={selectedVocabularyIds.includes(arg.id)}
                  addOrRemoveVocabulary={() => {
                    if (selectedVocabularyIds.includes(arg.id)) {
                      setSelectedVocabularyIds((prev) =>
                        prev.filter((entry) => entry !== arg.id),
                      );
                      setDeselectedVocabularyIds((prev) => [
                        ...prev.filter((entry) => entry !== arg.id),
                        arg.id,
                      ]);
                    } else {
                      setSelectedVocabularyIds((prev) =>
                        prev.includes(arg.id) ? prev : [...prev, arg.id],
                      );
                      setDeselectedVocabularyIds((prev) =>
                        prev.filter((entry) => entry !== arg.id),
                      );
                    }
                  }}
                  onJump={() => {
                    setFocusRequest({
                      word: arg.word,
                      sentence: arg.sentence ?? null,
                      token: Date.now(),
                    });
                  }}
                ></VocabularyItem>
              ))}
              {paginatedVocabularyList.hasNextPage && (
                <li className="bg-blue-500 p-3 rounded-lg shadow-lg mb-2 w-full">
                  <button
                    className="w-full bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition duration-200"
                    onClick={() => {
                      paginatedVocabularyList.fetchNextPage();
                    }}
                  >
                    Load more
                  </button>
                </li>
              )}
            </ul>
            {reepriseDetails.isSuccess && (
              <div className="md:hidden fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white px-5 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
                <button
                  className="w-full rounded-full border border-slate-300 bg-white px-3 py-2.5 text-[11px] uppercase tracking-[0.14em] text-slate-700"
                  onClick={() => {
                    void createChapterFromSelection();
                  }}
                >
                  {translate(chapterContinueButtonLabelKey)}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function FailedGeneratedTextScreen(props: {
  generatedTextId: number;
  supportEmail: string;
  contactHref: string;
  onRefresh: () => void;
}) {
  const { translate } = useTranslation();

  return (
    <div className="min-h-full bg-[radial-gradient(circle_at_top,rgba(186,230,253,0.55),transparent_38%),linear-gradient(180deg,#f8fafc_0%,#eef6ff_100%)] px-5 py-8 md:px-8">
      <div className="mx-auto max-w-3xl rounded-[32px] border border-sky-200/80 bg-white/90 p-6 md:p-10">
        <div className="mx-auto flex max-w-xl flex-col items-center text-center">
          <FailureIllustration />
          <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-sky-700">
            {translate("generated_text_failed_badge")}
          </p>
          <h1 className="mt-3 font-serif text-3xl text-slate-900 md:text-4xl">
            {translate("generated_text_failed_title")}
          </h1>
          <p className="mt-4 max-w-lg text-base leading-7 text-slate-700">
            {translate("generated_text_failed_body")}
          </p>
          <p className="mt-3 text-sm text-slate-500">
            {translate("generated_text_failed_contact_note", {
              email: props.supportEmail,
            })}
          </p>
          <div className="mt-8 flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
            <a
              href={props.contactHref}
              className="inline-flex min-h-12 items-center justify-center rounded-full border border-sky-300 bg-sky-100 px-6 text-base font-semibold text-sky-900 transition-colors hover:bg-sky-200"
            >
              {translate("generated_text_failed_contact")}
            </a>
            <button
              type="button"
              onClick={props.onRefresh}
              className="inline-flex min-h-12 items-center justify-center rounded-full border border-slate-300 bg-white px-6 text-base font-semibold text-slate-800 transition-colors hover:bg-slate-50"
            >
              {translate("generated_text_failed_refresh")}
            </button>
          </div>
          <Link
            to="/app/reeprise"
            className="mt-4 inline-flex min-h-12 items-center justify-center rounded-full px-5 text-base font-semibold text-slate-600 underline underline-offset-4"
          >
            {translate("generated_text_failed_back")}
          </Link>
          <p className="mt-8 text-xs uppercase tracking-[0.18em] text-slate-400">
            {`#${props.generatedTextId}`}
          </p>
        </div>
      </div>
    </div>
  );
}

function FailureIllustration() {
  return (
    <div
      aria-hidden="true"
      className="relative h-56 w-full max-w-[20rem] overflow-hidden rounded-[28px] border border-sky-200 bg-[linear-gradient(180deg,#dff3ff_0%,#f7fbff_100%)]"
    >
      <div className="absolute inset-x-6 bottom-0 h-24 rounded-t-[24px] border-x border-t border-slate-300 bg-white" />
      <div className="absolute left-1/2 top-[3.65rem] h-16 w-12 -translate-x-1/2 rounded-[16px] border border-slate-300 bg-slate-50" />
      <div className="absolute left-1/2 top-[4.55rem] h-1 w-8 -translate-x-1/2 rounded-full bg-slate-300" />
      <div className="absolute left-1/2 top-[5.2rem] h-1 w-6 -translate-x-1/2 rounded-full bg-slate-200" />
      <div className="absolute left-1/2 top-[1.25rem] h-20 w-20 -translate-x-1/2 rounded-full border border-amber-300 bg-[radial-gradient(circle,#fde68a_0%,#fcd34d_72%,#fbbf24_100%)]" />
      <div className="absolute left-[2.1rem] top-[6.2rem] h-12 w-20 rounded-full border border-white/80 bg-white/80" />
      <div className="absolute left-[3.4rem] top-[5.5rem] h-14 w-14 rounded-full border border-white/80 bg-white/90" />
      <div className="absolute right-[2rem] top-[5.7rem] h-12 w-20 rounded-full border border-white/80 bg-white/80" />
      <div className="absolute right-[3.2rem] top-[5.05rem] h-14 w-14 rounded-full border border-white/80 bg-white/90" />
      <div className="absolute bottom-[5.35rem] left-1/2 h-10 w-10 -translate-x-1/2 rounded-full border border-rose-300 bg-rose-100" />
      <div className="absolute bottom-[6.1rem] left-[7.6rem] h-4 w-4 rounded-full bg-white/80" />
      <div className="absolute bottom-[6.4rem] right-[7.5rem] h-4 w-4 rounded-full bg-white/80" />
      <div className="absolute bottom-[4.9rem] left-1/2 h-0.5 w-16 -translate-x-1/2 bg-rose-300" />
    </div>
  );
}

function formatAudioClock(rawSeconds: number) {
  if (!Number.isFinite(rawSeconds) || rawSeconds < 0) {
    return "0:00";
  }
  const totalSeconds = Math.floor(rawSeconds);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function VocabularyItem(props: {
  vocabulary: VocabularyDto;
  isSelected: boolean;
  addOrRemoveVocabulary: () => void;
  onJump: () => void;
}) {
  const [toggled, setToggled] = useState(false);
  const [showMoreGrammar, setShowMoreGrammar] = useLocalStorage<boolean>(
    "vocabulary-more-grammar-toggle-v2",
    false,
  );
  const { translate } = useTranslation();
  const translationDetails = normalizeVocabularyTranslationDetails({
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const primaryTranslation = getVocabularyPrimaryTranslation({
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const primaryWord = getVocabularyPrimaryWord({
    word: props.vocabulary.word,
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const surfaceWord = getVocabularySurfaceWord({
    word: props.vocabulary.word,
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const baseWord = getVocabularyBaseWord({
    word: props.vocabulary.word,
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const sourceSentence = props.vocabulary.sentence?.trim() || null;
  const translatedSentence = translationDetails.translated_sentence;
  const primarySentence = sourceSentence;
  const secondarySentence =
    translatedSentence && translatedSentence !== primaryTranslation
      ? translatedSentence
      : null;
  const hasExpandedGrammarDetails = Boolean(
    translationDetails.grammar.mood ||
      translationDetails.grammar.person ||
      translationDetails.grammar.case ||
      translationDetails.grammar.syntactic_function ||
      translationDetails.alternative_meanings ||
      translationDetails.source_lemma ||
      translationDetails.source_context_hint,
  );

  return (
    <li
      className={
        "group relative mb-4 rounded-xl border border-slate-200 bg-white p-3 sm:p-4 shadow-[0_1px_0_rgba(15,23,42,0.03)] " +
        (toggled ? "" : "cursor-pointer")
      }
      onClick={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest("[data-vocabulary-control='true']")) {
          return;
        }
        if (!toggled) {
          props.onJump();
        }
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <p className="m-0 px-1 font-serif text-xl font-semibold leading-snug text-slate-900">
            {primaryWord}
          </p>
          <div data-vocabulary-control="true">
            <VocabularyPronunciationButton vocabulary={props.vocabulary} />
          </div>
        </div>
        <input
          type="checkbox"
          data-vocabulary-control="true"
          className="appearance-none h-5 w-5 rounded-[6px] border border-slate-300 bg-white transition-colors duration-150 cursor-pointer relative
            hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2
            checked:bg-slate-900 checked:border-slate-900
            checked:after:content-[''] checked:after:absolute checked:after:w-[6px] checked:after:h-[10px] checked:after:border-r-[2px] checked:after:border-b-[2px] checked:after:border-white checked:after:rotate-45 checked:after:left-1/2 checked:after:top-1/2 checked:after:-translate-x-1/2 checked:after:-translate-y-[55%]"
          checked={props.isSelected}
          onChange={props.addOrRemoveVocabulary}
          onClick={(event) => event.stopPropagation()}
        />
      </div>
      <p className="mb-2 mt-0 px-1 font-serif text-lg font-normal leading-snug text-slate-600">
        {primaryTranslation}
      </p>
      {surfaceWord !== primaryWord && (
        <p className="mb-2 px-1 text-xs text-slate-600">
          <span className="font-semibold">
            {translate("vocabulary_label_seen_form")}:
          </span>{" "}
          {surfaceWord}
        </p>
      )}
      <div className="mt-3 border-y border-slate-200 py-2 space-y-1">
        <p className="m-0 text-left font-serif text-sm font-normal leading-snug text-slate-700">
          {primarySentence
            ? highlightVocabularyWordInSentence(primarySentence, surfaceWord)
            : translate("vocabulary_source_sentence_unavailable")}
        </p>
        {secondarySentence && (
          <p className="m-0 text-left font-serif text-sm font-normal leading-snug text-slate-500">
            {secondarySentence}
          </p>
        )}
      </div>
      <div className="mt-3 border-y border-slate-200 py-2 space-y-2">
        {baseWord && (
          <p className="px-1 text-xs text-slate-600">
            <span className="font-semibold">
              {translate("vocabulary_label_base_form")}:
            </span>{" "}
            {baseWord}
          </p>
        )}
        <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
          {translate("vocabulary_label_grammar")}
        </p>
        <div className="flex flex-wrap gap-2 text-xs">
          {translationDetails.grammar.part_of_speech && (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700">
              {translate("vocabulary_label_pos")}:{" "}
              {translationDetails.grammar.part_of_speech}
            </span>
          )}
          {translationDetails.grammar.tense && (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700">
              {translate("vocabulary_label_tense")}:{" "}
              {translationDetails.grammar.tense}
            </span>
          )}
          {translationDetails.grammar.number && (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700">
              {translate("vocabulary_label_number")}:{" "}
              {translationDetails.grammar.number}
            </span>
          )}
          {translationDetails.grammar.gender && (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700">
              {translate("vocabulary_label_gender")}:{" "}
              {translationDetails.grammar.gender}
            </span>
          )}
          {translationDetails.source_plural_form && (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700">
              {translate("vocabulary_label_plural")}:{" "}
              {translationDetails.source_plural_form}
            </span>
          )}
        </div>
        {showMoreGrammar && hasExpandedGrammarDetails && (
          <div className="grid gap-1 text-xs text-slate-600">
            {translationDetails.grammar.mood && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_mood")}:
                </span>{" "}
                {translationDetails.grammar.mood}
              </p>
            )}
            {translationDetails.grammar.person && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_person")}:
                </span>{" "}
                {translationDetails.grammar.person}
              </p>
            )}
            {translationDetails.grammar.case && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_case")}:
                </span>{" "}
                {translationDetails.grammar.case}
              </p>
            )}
            {translationDetails.grammar.syntactic_function && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_syntactic_function")}:
                </span>{" "}
                {translationDetails.grammar.syntactic_function}
              </p>
            )}
            {translationDetails.alternative_meanings && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_alternatives")}:
                </span>{" "}
                {translationDetails.alternative_meanings}
              </p>
            )}
            {translationDetails.source_lemma &&
              translationDetails.source_lemma !== primaryWord &&
              translationDetails.source_lemma !== baseWord && (
                <p>
                  <span className="font-semibold">
                    {translate("vocabulary_label_lemma")}:
                  </span>{" "}
                  {translationDetails.source_lemma}
                </p>
              )}
            {translationDetails.source_context_hint && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_context_hint")}:
                </span>{" "}
                {translationDetails.source_context_hint}
              </p>
            )}
          </div>
        )}
        {hasExpandedGrammarDetails && (
          <div className="flex justify-end">
            <button
              type="button"
              data-vocabulary-control="true"
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
                setShowMoreGrammar((prev) => !prev);
              }}
              className="rounded-full border border-slate-300 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-50 focus-visible:outline-none"
            >
              {showMoreGrammar
                ? translate("vocabulary_less_grammar")
                : translate("vocabulary_more_grammar")}
            </button>
          </div>
        )}
      </div>
      

      {toggled ? (
        <MnemonicEditForm vocabulary={props.vocabulary} />
      ) : (
        <p className="mt-2 px-1 font-serif text-base font-normal leading-snug text-slate-700">
          {props.vocabulary.mnemonic}
        </p>
      )}
      <div className="flex justify-between my-2">
        <button
          data-vocabulary-control="true"
          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-base hover:bg-slate-50"
          onClick={(event) => {
            event.stopPropagation();
            setToggled(!toggled);
          }}
        >
          {toggled ? "❌" : "🖊️"}
        </button>
      </div>
    </li>
  );
}

type MnemonicFormValues = {
  mnemonic: string;
};

function MnemonicEditForm(props: { vocabulary: VocabularyDto }) {
  const updateVocabulary = useVocabularyUpdate();
  const { register, handleSubmit } = useForm<MnemonicFormValues>();
  const onSubmit: SubmitHandler<MnemonicFormValues> = (data) => {
    void updateVocabulary.mutateAsync({
      id: props.vocabulary.id,
      patchVocabularyDto: {
        mnemonic: data.mnemonic,
      },
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex justify-between items-center"
    >
      <textarea
        {...register("mnemonic")}
        rows={5}
        cols={40}
        className="grow p-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
        defaultValue={props.vocabulary.mnemonic ?? ""}
      ></textarea>
      <input
        type="submit"
        value="☑️"
        className="m-2 text-2xl border-gray-300"
      />
    </form>
  );
}

function LastChapterNavigationTile(props: {
  activeChapterNumber: number | undefined;
  generatedTextId: number;
}) {
  const { translate } = useTranslation();
  const previousChapterNumber =
    props.activeChapterNumber === undefined
      ? undefined
      : props.activeChapterNumber - 1;

  if (previousChapterNumber !== undefined) {
    return (
      <Link
        to="/app/reeprise/$generatedTextId"
        params={{
          generatedTextId: props.generatedTextId.toString(),
        }}
        search={{
          chapter:
            previousChapterNumber === -1 ? undefined : previousChapterNumber,
          focusWord: undefined,
          focusSentence: undefined,
          focusToken: undefined,
        }}
        className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        <span>{translate("chapter_previous")}</span>
      </Link>
    );
  }

  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-slate-300">
      <ChevronLeftIcon className="h-4 w-4" />
      <span>{translate("chapter_previous")}</span>
    </div>
  );
}

function NextChapterNavigationTile(props: {
  activeChapterNumber: number | undefined;
  generatedTextId: number;
}) {
  const { translate } = useTranslation();
  const nextChapterNumber =
    props.activeChapterNumber === undefined ? 0 : props.activeChapterNumber + 1;

  const nextChapterQuery = useChapterRetrieveQuery({
    chapterCounter: nextChapterNumber,
    generatedTextId: props.generatedTextId,
  });

  if (
    nextChapterQuery.data !== undefined &&
    nextChapterQuery.data.status !== "NON_EXISTENT"
  ) {
    return (
      <Link
        to="/app/reeprise/$generatedTextId"
        params={{
          generatedTextId: props.generatedTextId.toString(),
        }}
        search={{
          chapter: nextChapterNumber,
          focusWord: undefined,
          focusSentence: undefined,
          focusToken: undefined,
        }}
        className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
      >
        <span>{translate("chapter_next")}</span>
        <ChevronRightIcon className="h-4 w-4" />
      </Link>
    );
  }

  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-slate-300">
      <span>{translate("chapter_next")}</span>
      <ChevronRightIcon className="h-4 w-4" />
    </div>
  );
}
